import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, Heart, Wind, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SleepMeditationGuide() {
  const navigate = useNavigate();

  const bodyScanSteps = [
    { part: "Feet & Toes", instruction: "Notice any tension. Imagine warmth flowing in. Let them relax completely." },
    { part: "Calves & Shins", instruction: "Feel the weight of your legs on the bed. Release any holding." },
    { part: "Thighs & Hips", instruction: "These muscles work hard all day. Let them soften and sink down." },
    { part: "Lower Back & Belly", instruction: "Let your belly rise and fall naturally. No need to hold anything in." },
    { part: "Chest & Upper Back", instruction: "Feel your heart beating slowly. Let your shoulders drop away from ears." },
    { part: "Hands & Arms", instruction: "Unclench your fists. Let your arms be heavy and still." },
    { part: "Neck & Jaw", instruction: "Unclench your jaw. Let your tongue rest. Soften your throat." },
    { part: "Face & Head", instruction: "Smooth your forehead. Relax your eyes. Let go of the day." }
  ];

  const breathingTechniques = [
    { name: "4-7-8 Breath", steps: "Inhale 4 counts, hold 7 counts, exhale 8 counts", best: "Falling asleep" },
    { name: "Box Breathing", steps: "Inhale 4, hold 4, exhale 4, hold 4", best: "Calming anxiety" },
    { name: "Belly Breathing", steps: "Place hand on belly, breathe so hand rises", best: "Deep relaxation" }
  ];

  const appSuggestions = [
    "Calm - Sleep Stories & Meditations",
    "Headspace - Sleep Sleepcasts",
    "Insight Timer - Free guided meditations",
    "YouTube - 'Sleep meditation' or 'Body scan'"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">
            Week 5 • Day 7 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Sleep Meditation Practice
          </h1>
          <p className="text-xl text-secondary-600">Drift Off with Guided Relaxation</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Moon className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Tonight's Practice</h3>
              <p className="text-indigo-100 text-lg">
                Try a guided sleep meditation or body scan. Lie in bed, close your eyes, and let a calm voice 
                guide you to sleep. This can decrease time to fall asleep by 50%.
              </p>
            </div>
          </div>
        </section>

        {/* Body Scan */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Heart className="w-6 h-6 text-indigo-500" />
            DIY Body Scan Meditation
          </h2>
          <p className="text-secondary-600 mb-4">
            Lie comfortably in bed. Starting from your toes, slowly move your attention up through your body:
          </p>
          <div className="space-y-3">
            {bodyScanSteps.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-3 bg-indigo-50 rounded-lg">
                <span className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">
                  {index + 1}
                </span>
                <div>
                  <h3 className="font-bold text-indigo-700">{item.part}</h3>
                  <p className="text-sm text-secondary-600">{item.instruction}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Breathing Techniques */}
        <section className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border-2 border-indigo-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Wind className="w-6 h-6 text-indigo-500" />
            Breathing Techniques
          </h2>
          <div className="space-y-4">
            {breathingTechniques.map((tech, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-indigo-700 mb-1">{tech.name}</h3>
                <p className="text-secondary-700 mb-1">{tech.steps}</p>
                <p className="text-sm text-secondary-500">Best for: {tech.best}</p>
              </div>
            ))}
          </div>
        </section>

        {/* App Suggestions */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-500" />
            Guided Meditation Resources
          </h2>
          <div className="space-y-3">
            {appSuggestions.map((app, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg text-secondary-700">
                <CheckCircle className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                {app}
              </div>
            ))}
          </div>
          <div className="mt-6 bg-indigo-100 rounded-xl p-4">
            <p className="text-secondary-700">
              <strong>Tip:</strong> Set your phone to airplane mode after starting the meditation 
              so you're not disturbed. Use headphones for better immersion.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}

export default SleepMeditationGuide;
